import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  IconButton,
  LinearProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';

const DEFAULT_DURATION = 10000;

const ErrorToastContext = createContext(null);

export const ErrorToastProvider = ({ children }) => {
  const [toast, setToast] = useState({
    open: false,
    message: '',
    duration: DEFAULT_DURATION,
    id: 0,
    severity: 'error',
  });
  const [progress, setProgress] = useState(100);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const showErrorToast = useCallback((message, options = {}) => {
    const duration = Number.isFinite(options.duration) ? options.duration : DEFAULT_DURATION;
    setToast({
      open: true,
      message,
      duration,
      id: Date.now(),
      severity: 'error',
    });
  }, []);

  const showSuccessToast = useCallback((message, options = {}) => {
    const duration = Number.isFinite(options.duration) ? options.duration : DEFAULT_DURATION;
    setToast({
      open: true,
      message,
      duration,
      id: Date.now(),
      severity: 'success',
    });
  }, []);

  const handleClose = useCallback((event, reason) => {
    if (reason === 'clickaway') return;
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    if (!toast.open) {
      setProgress(100);
      clearTimer();
      return;
    }

    const startTime = Date.now();
    const duration = toast.duration || DEFAULT_DURATION;
    setProgress(100);
    clearTimer();

    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(duration - elapsed, 0);
      setProgress((remaining / duration) * 100);

      if (remaining <= 0) {
        clearTimer();
      }
    }, 100);

    timeoutRef.current = setTimeout(() => {
      handleClose();
    }, duration);

    return clearTimer;
  }, [toast.open, toast.duration, toast.id, clearTimer, handleClose]);

  const contextValue = useMemo(() => ({ showErrorToast, showSuccessToast }), [showErrorToast, showSuccessToast]);

  const getIcon = () => {
    if (toast.severity === 'success') {
      return <CheckCircleIcon />;
    }
    return <ErrorOutlineIcon />;
  };

  return (
    <ErrorToastContext.Provider value={contextValue}>
      {children}
      <Snackbar
        key={toast.id}
        open={toast.open}
        autoHideDuration={toast.duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          icon={getIcon()}
          onClose={handleClose}
          action={
            <IconButton aria-label="Close" color="inherit" size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{
            alignItems: 'flex-start',
            minWidth: 280,
            maxWidth: 420,
          }}
        >
          <Box sx={{ width: '100%', mb: 1 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              color="inherit"
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: 'rgba(255, 255, 255, 0.35)',
              }}
            />
          </Box>
          <Typography variant="body2">{toast.message}</Typography>
        </Alert>
      </Snackbar>
    </ErrorToastContext.Provider>
  );
};

export const useErrorToast = () => {
  const context = useContext(ErrorToastContext);
  if (!context) {
    throw new Error('useErrorToast must be used within an ErrorToastProvider');
  }
  return context;
};
